import { Stack, Typography } from "@mui/material";

import { DocumentGroup, DocumentResponse } from "@/api";
import { FileActions } from "@/common/components/layout/components/dropzone/DropZone.types";
import { PendingDocumentMove } from "@/common/components/layout/components/dropzone/dnd/DropZoneDnd.types";
import { usePendingDocumentMovesContext } from "@/common/components/layout/components/dropzone/dnd/PendingDocumentMovesContext";
import { PendingMoveRow } from "@/common/components/layout/components/dropzone/dnd/components/pending-move-row/PendingMoveRow";
import { useTranslation } from "@/translations";

import { DocumentWidgetUploading } from "./components/document-widget-uploading/DocumentWidgetUploading";
import { DocumentWidget } from "./components/document-widget/DocumentWidget";

interface Props {
  files: DocumentResponse[];
  uploadingFiles: File[];
  pendingIncomingMoves: PendingDocumentMove[];
  selectedFiles: Set<string>;
  onToggleSelect: (fileId: string) => void;
  fileActions: FileActions;
  sourceZone: DocumentGroup;
}

export function DocumentList({
  files,
  uploadingFiles,
  pendingIncomingMoves,
  selectedFiles,
  onToggleSelect,
  fileActions,
  sourceZone,
}: Props) {
  const { t } = useTranslation();
  const { removePendingMove } = usePendingDocumentMovesContext();
  const isEmpty = files.length === 0 && uploadingFiles.length === 0 && pendingIncomingMoves.length === 0;

  return (
    <Stack gap={1}>
      {isEmpty ? (
        <Typography variant="body2" color="text.disabled" sx={{ alignSelf: "center" }}>
          {t("dropzone.panel.empty")}
        </Typography>
      ) : (
        <>
          {files.map((file) => (
            <DocumentWidget
              key={file.documentId}
              file={file}
              selected={selectedFiles.has(file.documentId)}
              onToggleSelect={onToggleSelect}
              fileActions={fileActions}
              sourceZone={sourceZone}
            />
          ))}
          {uploadingFiles.map((file) => (
            <DocumentWidgetUploading key={`${file.name}-${file.lastModified}`} file={file} />
          ))}
          {pendingIncomingMoves.map((move) => (
            <PendingMoveRow key={move.document.documentId} move={move} onCancel={() => removePendingMove(move)} />
          ))}
        </>
      )}
    </Stack>
  );
}
